import React from 'react';
import { createPortal } from 'react-dom';
import styles from './DevFooterPortalHost.module.scss';

export interface DevFooterPortalHostProps {
  children: React.ReactNode;
}

/**
 * Renders the dev footer as its own top-level layer, fully isolated from the app shell.
 * Three mechanisms, each load-bearing:
 * 1. **Portal to `document.body`** — the footer leaves `#root` entirely. This is what fixes the
 *    blur: overlay scrims (`Modal`, `Dialog`, `AlertDialog`) are body-level portals painting
 *    `backdrop-filter: blur()`, and a filter blurs everything painted *behind* it. The whole
 *    `#root` subtree paints before a body-level sibling, so no z-index inside `#root` can escape.
 * 2. **Native top layer** via `popover="manual"` — paints above every z-index, so the footer stays
 *    above overlays without owning a number in a z-index race. Falls back to the stylesheet's
 *    `z-index: 10000` where the top layer is unavailable (jsdom, older engines).
 * 3. **`aria-live`** — Base UI's `markOthers` seeds its keep-list from
 *    `body.querySelectorAll('[aria-live]')`, so an open modal does not stamp `aria-hidden="true"`
 *    on this host. That keeps the footer's buttons reachable, and avoids putting `aria-hidden` on
 *    a container with focusable descendants.
 * Side effect of (2) + (3): a click on the footer is not an outside-press, so a modal stays open
 * while you use the footer.
 * The host also publishes its own height as `--dev-footer-height` on `document.documentElement`, so
 * bottom-anchored layers outside the app shell (the `<Toaster>`) can sit above the footer at the
 * same gap they'd have from the viewport edge in prod, where no footer exists.
 * @param props - Component props holding the footer to render.
 */
export const DevFooterPortalHost: React.FC<DevFooterPortalHostProps> = (props: DevFooterPortalHostProps) => {
  const { children } = props;
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  React.useEffect(() => {
    if (!mounted) return;
    const host = hostRef.current;
    if (!host) return;
    /**
     * On any failure to promote, strip the attribute — otherwise the UA rule
     * `[popover]:not(:popover-open) { display: none }` hides the footer outright. The stylesheet's
     * z-index fallback then carries it.
     */
    if (typeof host.showPopover !== 'function') {
      host.removeAttribute('popover');
      return;
    }
    try {
      host.showPopover();
    } catch {
      host.removeAttribute('popover');
      return;
    }
    /** Hiding on cleanup keeps StrictMode's mount → cleanup → mount cycle from re-showing an
     * already-open popover, which throws `InvalidStateError`. */
    return () => host.hidePopover();
  }, [mounted]);
  /**
   * Publish the footer's rendered height so bottom-anchored body-level layers can clear it. Measured
   * rather than hardcoded — the app and site footers have different heights. Re-measured by a
   * ResizeObserver (font size / density changes are not render-time deps), and cleared on unmount so
   * a prod build never leaves a stale offset behind. Guarded for jsdom, which has no ResizeObserver.
   */
  React.useEffect(() => {
    if (!mounted) return;
    const host = hostRef.current;
    if (!host) return;
    const measure = () =>
      document.documentElement.style.setProperty(
        '--dev-footer-height',
        `${Math.round(host.getBoundingClientRect().height)}px`
      );
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(host);
    return () => {
      observer?.disconnect();
      document.documentElement.style.removeProperty('--dev-footer-height');
    };
  }, [mounted]);
  if (!mounted) return null;
  return createPortal(
    <div ref={hostRef} className={styles.host} popover="manual" aria-live="off">
      {children}
    </div>,
    document.body
  );
};
